import type { Edge, Node, NodeType } from '@/components/diagrams/FlowChart';
import type { DiagramNodeHistory } from '@/components/diagrams/FlowChart/utils/nodeHistory';
import {
  DiagramDispositionsFormState,
  NonRootRecipientOverride,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { VisualizationGroup } from '@/modules/graphViz/graphVizGroup/grouping';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath } from '@/types/schema';

import { RecipientDispositions } from '../getDispositionsVizFromEntity';
import { CltTermDiagram_EntityFragment } from '../graphql/CltTermDiagram.generated';
import { CrtTermDiagram_EntityFragment } from '../graphql/CrtTermDiagram.generated';
import { DispositionsDiagram_EntityFragment } from '../graphql/DispositionsDiagram.generated';
import { EntityDiagram_NodeConfigurationFragment } from '../graphql/EntityDiagram.generated';
import { GratTermDiagram_EntityFragment } from '../graphql/GratTermDiagram.generated';
import { OwnershipDiagram_EntityFragment } from '../graphql/OwnershipDiagram.generated';
import { QprtTermDiagram_EntityFragment } from '../graphql/QprtTermDiagram.generated';
import { EntityDiagramGraph } from './entityDiagramGraph';
import { EntityDiagramGraphNodeAttributes } from './entityDiagramGraph';

export enum EntityDiagramVariant {
  Dispositions = 'DISPOSITIONS', // Show the first and second order disposition recipients
  Ownership = 'OWNERSHIP', // Show the owners of the entity
  GratTerm = 'GRAT_TERM', // Show the GRAT grantor and remainder trust
  QprtTerm = 'QPRT_TERM', // Show the QPRT grantors and remainder trust
  CrtTerm = 'CRT_TERM', // Show the CRT grantors and remainder trust
  CltTerm = 'CLT_TERM', // Show the CLT grantors and remainder trust
}

export interface EntityDiagramStateProps {
  primaryClients: ContextPrimaryClient[];
  entity:
    | DispositionsDiagram_EntityFragment
    | OwnershipDiagram_EntityFragment
    | GratTermDiagram_EntityFragment
    | QprtTermDiagram_EntityFragment
    | CrtTermDiagram_EntityFragment
    | CltTermDiagram_EntityFragment;
  isTwoClientHousehold: boolean;
  /** presentationMode is used to disable interactivity with the graph */
  presentationMode: boolean;
  isOnDispositionsFormDiagram?: boolean;
  /**
   * When true, the diagram renders inside a tall half-width container
   * (e.g. the DP V2 form split-screen, or a presentation slide with
   * notes). Enables aspect-ratio-aware vertical stretching so the
   * diagram fills more of the container for recipient-heavy layouts.
   */
  isInHalfWidthContainer?: boolean;
  /**
   * Measured container aspect ratio (width / height) used as the
   * stretch target when non-null. Only set in contexts where the
   * container size varies freely (e.g. DP V2 split-screen); fixed
   * layouts like presentation slides leave this unset and fall back
   * to the {@link SINGLE_ENTITY_DIAGRAM_TARGET_ASPECT_RATIO} constant.
   */
  containerAspectRatio?: number;
  /** viewOnlyMode is used to disable editing */
  viewOnlyMode: boolean;
  selectedPrimaryClientId: string;
  entityDiagramVariant: EntityDiagramVariant;
  entityDiagramId?: string | null;
  nodeConfigurations: EntityDiagram_NodeConfigurationFragment[];
  visualizationGroups: VisualizationGroup[];
  originalVisualizationGroups: VisualizationGroup[];
  hideEntitiesListInGroups: boolean;
  baseEdges: Edge[];
  /** Optional dispositions of type DiagramDispositionsFormState */
  externalDispositions?: DiagramDispositionsFormState;
  /** Optional dispositions for items in externalDispositions */
  recipientDispositions?: RecipientDispositions;
  /**
   * Live overlay for a BDG entity edited as a non-root target. Overrides the
   * server-resolved dispositions for `nonRootRecipientOverride.entityId` only,
   * leaving the root's `externalDispositions` overlay untouched.
   */
  nonRootRecipientOverride?: NonRootRecipientOverride | null;
  initialSelectedNode?: {
    pulid: string;
    afterDeath: AfterDeath;
  } | null;
  /**
   * For BDG-aware (retirement / DH-insurance) entities with a clear
   * privileged trigger client (single owner / single policy holder who is
   * a primary client), the client id in the OFF position of the
   * death-order Switch. Null when there's no clear default (survivorship,
   * multi-holder, non-primary-client owner); the Switch then falls back to
   * `primaryClients[0]`. See `getBdgTriggerInfo`.
   */
  bdgDefaultTriggerClientId?: string | null;
  /**
   * True for any BDG-aware (retirement / DH-insurance) entity. Drives
   * Switch-vs-ButtonGroup selection in the death-order toggle.
   */
  isBdgEntity?: boolean;
}

export interface EntityDiagramDerivedData {
  isSummaryPanelOpen: boolean;
  summaryNode: EntityDiagramGraphNodeAttributes | null;
  nodesByType: Partial<Record<NodeType, Node[]>>;
  nodesBySectionId: Record<string, Node[]>;
  nodesById: Record<string, Node>;
  edgesById: Record<string, Edge>;
}

export interface EntityDiagramState extends EntityDiagramStateProps {
  entityDiagramGraph: EntityDiagramGraph;
  isInitializing: boolean;
  nodes: Node[];
  edges: Edge[];
  /** The id of the node whose summary panel is currently open */
  summaryNodeId: string | null;
  highlightedIds: Set<string>;
  derivedData: EntityDiagramDerivedData;
  isExporting: boolean;
  opacity: number;
  entityDiagramId: string | null;
  nodeConfigurations: EntityDiagram_NodeConfigurationFragment[];
  renderWhiteLabeled: boolean;
  nodeHistory: DiagramNodeHistory;
}
